import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {
    Container,
    ContainerChangePassword,
    ContainerInputs,
    Content,
    ContentReSend,
    Section,
    SubTitleConfirmEmail,
    TextLink
} from "@dropDesk/presentation/pages/change_password/ui/styled";
import forgotImage from "@dropDesk/storage/images/forgot.png";
import activateImage from "@dropDesk/storage/images/activate.png";
import Header from "@dropDesk/presentation/components/headers/header_secondary";
import React, {FormEvent, useEffect, useState} from "react";
import HeaderForm from "@dropDesk/presentation/pages/change_password/ui/header_form";
import Inputs from "@dropDesk/presentation/pages/change_password/ui/inputs";
import ButtonChangePassword from "@dropDesk/presentation/pages/change_password/ui/button_change_password";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {useInjection} from "inversify-react";
import {UserController} from "@dropDesk/presentation/pages/user/controller/user.controller";
import {useTranslation} from "react-i18next";
import {LoginController} from "@dropDesk/presentation/pages/login/controller/login.controller";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {isValidPassword} from "@dropDesk/utils/helpers/validators";
import {observer} from "mobx-react";
import AnimatedImage from "@dropDesk/presentation/pages/change_password/ui/animate_image";
import DropDeskLoading from "@dropDesk/presentation/components/loadings/loading_dropdesk";
import {RoutesEnum} from "@dropDesk/domain/entities/routes/routes_enum";
import {MailAction} from "@dropDesk/domain/entities/common/actions_entity";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";

const ChangePassword = (observer(() => {


    const [password, setPassword] = useState<string | undefined>(undefined);
    const [token, setToken] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errorMessagePassword, setErrorMessagePassword] = useState("");
    const [errorMessageConfirmPassword, setErrorMessageConfirmPassword] = useState("");
    const [errorMessageToken, setErrorMessageToken] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [isValidDataForm, setIsValidDataForm] = useState<boolean>(false);
    const [hasSendEmail, setHasSendEmail] = useState<boolean>(false);


    const userController = useInjection(UserController);
    const loginController = useInjection(LoginController);
    const appController = useInjection(AppController);

    const {pathname} = useLocation();
    const [searchParams] = useSearchParams();
    const tokenParams = searchParams.get("token");
    const emailParams = searchParams.get("email");
    const isConfirmation = !!searchParams.get("confirmation");
    const isFirstAccess: boolean = pathname.includes("first-access");
    const isChangePassword: boolean = pathname.includes("change-password");
    const isMigrated: boolean = pathname.includes("migrated");
    const tokenInputVisible: boolean = !tokenParams;
    const colors = appController.theme.colorScheme;
    const navigate = useNavigate();
    const {t} = useTranslation();


    const initialize = () => {
        if (isConfirmation) {
            const token = tokenParams ? window.atob(tokenParams) : "";
            setToken(token);
        }
    }

    useEffect(() => {
        initialize();
    }, []);

    useEffect(() => {
        validate();
    }, [password, confirmPassword, passwordVisible, token]);

    const setVerifyCurrentUser = () => {
        const userLogged = appController.user!.copyWith({
            verified: true
        });
        appController.setUser(userLogged);
    }

    function validate() {
        const validPassword = isValidPassword(password ?? '');
        const validConfirmPassword = isValidPassword(confirmPassword) && confirmPassword === password;
        setErrorMessagePassword("");
        setErrorMessageConfirmPassword("");
        setErrorMessageToken("");

        if (!token && tokenInputVisible) {
            setErrorMessageToken("Informe o código");
            setIsValidDataForm(false);
        } else if (token.length < 6 && tokenInputVisible) {
            setErrorMessageToken("Código inválido");
            setIsValidDataForm(false);
        } else if (!validPassword && !isConfirmation) {
            setErrorMessagePassword(!password ? "Informe uma senha" : "Senha menor que 6 dígitos");
            setIsValidDataForm(false);
        } else if (validPassword && !validConfirmPassword && !isConfirmation) {
            setErrorMessageConfirmPassword(!confirmPassword ? "Informe uma senha" : confirmPassword !== password ? "Senhas diferentes" : "Senha menor que 6 dígitos");
            setIsValidDataForm(false);
        } else if (!emailParams && isMigrated) {
            setErrorMessageConfirmPassword("Informe um email");
            setIsValidDataForm(false);
        } else {
            setIsValidDataForm(true);
        }
    };

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        if (isValidDataForm) {
            const renewToken = !!tokenParams ? window.atob(tokenParams) : token.trim();
            if (isFirstAccess) {
                await userController.firstAccess(renewToken, onSuccess, password);
            } else if (isMigrated) {
                await loginController.migrate(emailParams!, password!, renewToken, onSuccessMigrate);
            } else {
                await userController.changePassword(renewToken, password ?? '', onSuccess);
            }
        }
    }

    const onSuccess = async (key: string, email: string) => {
        if (email && password) {
            await loginController.login({email, password}, onSuccessLogin);
        } else {
            setVerifyCurrentUser();
            navigate(RoutesEnum.dashboard, {replace: true});
        }
        toastMessage.success(t(key));
    }

    const onSuccessMigrate = async (key: string, user: UserEntity) => {
        const route = appController.setCredentials(user);
        navigate(route, {replace: true});
        toastMessage.success(t(key));
    }

    const onSuccessLogin = async () => {
        const route = await appController.getCurrentUser();
        navigate(route, {replace: true});
    }

    const onSuccessSendEmailForgot = async (key: string) => {
        setHasSendEmail(true);
        toastMessage.success(t(key));
    }


    return (
        <Container>

            <AnimatedImage
                imageSource={isFirstAccess ? activateImage : forgotImage}
                title={isFirstAccess ? "Ativação de conta" : isChangePassword ? "Esqueceu sua senha?" : "Atualize sua senha para prosseguir"}
                subTitle={isFirstAccess ?
                    `Para que você tenha toda segurança dentro de nossa plataforma, precisamos nesse primeiro acesso,
           criar uma senha para você, informe sua nova senha.` :
                    isChangePassword ? "Para recuperar seu acesso á conta, preencha os campos." :
                        "Sua conta foi migrada com sucesso! Para garantir sua segurança, é necessário definir uma nova senha antes de prosseguir."}
            />

            <Section background={colors.delicateBackground}>
                {!isMigrated && <Header background={colors.delicateBackground} color={colors.text}/>}

                <Content background={colors.delicateBackground}>

                    <ContainerChangePassword onSubmit={handleSubmit} background={colors.delicateBackground}>

                        <HeaderForm
                            isConfirmation={isConfirmation}
                            textHeader={isFirstAccess ? "Ativação de conta" : isChangePassword ? "Recuperar Senha" : "Atualizar senha"}
                            hasDefaultToken={!!tokenParams}
                            isMigrated={isMigrated}
                        />

                        <ContainerInputs>
                            <Inputs
                                validate={validate}
                                tokenEntity={{token, setToken, errorMessageToken}}
                                passwordEntity={{password, setPassword, errorMessagePassword}}
                                confirmPasswordEntity={{
                                    confirmPassword,
                                    setConfirmPassword,
                                    errorMessageConfirmPassword
                                }}
                                visiblePasswordEntity={{passwordVisible, setPasswordVisible}}
                                loading={(loginController.loading || appController.loading || userController.loading)}
                                tokenInputVisible={(tokenInputVisible || isConfirmation)}
                                isConfirmation={isConfirmation}
                                defaultValue={tokenParams ? window.atob(tokenParams) : ""}
                            />
                            <ButtonChangePassword
                                handleSubmit={handleSubmit}
                                loading={(loginController.loading || appController.loading || userController.loading)}
                                isValidForm={isValidDataForm}
                                textButton={isFirstAccess ? "Ativar conta" : "Alterar senha"}
                            />
                        </ContainerInputs>

                        {emailParams && <ContentReSend>
                            <SubTitleConfirmEmail color={colors.text}>
                                Se você não tiver recebido o e-mail, procure na pasta de spam ou&nbsp;
                                {!hasSendEmail && <TextLink
                                    disabled={userController.loading}
                                    onClick={async () => {
                                        if (isMigrated) {
                                            await loginController.startMigration(emailParams, onSuccessSendEmailForgot, MailAction.resend);
                                        } else {
                                            await userController.forgot(emailParams, onSuccessSendEmailForgot, true);
                                        }
                                    }}
                                    color={colors.text}
                                >reenvie o e-mail.</TextLink>}
                                {hasSendEmail && <SubTitleConfirmEmail color={colors.text}>
                                    e-mail já foi enviado por favor verifique.
                                </SubTitleConfirmEmail>
                                }
                            </SubTitleConfirmEmail>
                        </ContentReSend>}

                    </ContainerChangePassword>

                </Content>
            </Section>
            {(loginController.loading || appController.loading || userController.loading) && <DropDeskLoading
                height={250}
                description={loginController.loadingMessage ?? appController.loadingMessage ?? userController.loadingMessage}
            />}
        </Container>
    );
}));

export default ChangePassword;
